import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useToasts } from 'react-toast-notifications'

import { getOtherHotelDetails, updatePlan } from '../../actions/hotelDetails'
import hotelApi from 'services/api/hotel'
import { buildManagePlanPayload } from './buildManagePlanPayload'
import { isDowngradeChange } from './isDowngradeChange'
import {
  shouldConfirmDowngradeNextCycle,
  shouldLockDowngradeToNextCycle
} from './shouldConfirmDowngradeNextCycle'
import { getSuggestedEngageStylePlanName } from './suggestedCustomPlanName'
import {
  ALL_SERVICES,
  BILLING_OPTION_MONTHLY,
  BILLING_OPTIONS,
  BILLING_OPTIONS_MAIN,
  fmt,
  PLAN_LOCKED,
  PLANS,
  PROPERTY as DEFAULT_PROPERTY
} from './constants'
import styles from './ManagePlan.module.scss'

const PAYMENT_PERIOD_TO_BILLING_ID = {
  Monthly: 'monthly',
  Quarterly: 'quarterly',
  SemiAnnual: 'semiAnnual',
  Annual: 'annual'
}

const BILLING_ID_TO_API = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semiAnnual: 'SemiAnnual',
  annual: 'Annual'
}

const BILLING_PERIOD_MONTHS = {
  monthly: 1,
  quarterly: 3,
  semiAnnual: 6,
  annual: 12
}

function addMonthsSafe(date, monthsToAdd) {
  const d = new Date(date.getTime())
  d.setMonth(d.getMonth() + monthsToAdd)
  return d
}

const GRACE_DAYS_RECOMMENDED_MAX = 14
const GRACE_DAYS_WARNING_MAX = 30

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionTitle({ step, children, subtitle }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div
          style={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: '#1E293B',
            color: '#fff',
            fontSize: 11,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: "'DM Mono', monospace",
            flexShrink: 0
          }}
        >
          {step}
        </div>
        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: '#1E293B',
            letterSpacing: '0.01em'
          }}
        >
          {children}
        </span>
      </div>
      {subtitle && (
        <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4, marginLeft: 32 }}>
          {subtitle}
        </div>
      )}
    </div>
  )
}

function Card({ children, style }) {
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #E2E8F0',
        boxShadow: '0px 4.86727px 19.4691px rgba(0, 0, 0, 0.05)',
        borderRadius: 4,
        padding: '22px 24px',
        marginBottom: 16,
        ...style
      }}
    >
      {children}
    </div>
  )
}

function Divider({ style }) {
  return <div style={{ borderTop: '1px solid #F1F5F9', margin: '16px 0', ...style }} />
}

function Tag({ children, color }) {
  return (
    <span
      style={{
        background: (color || '#4079F5') + '15',
        color: color || '#4079F5',
        border: `1px solid ${color || '#4079F5'}25`,
        borderRadius: 4,
        padding: '2px 8px',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.07em',
        fontFamily: "'DM Mono', monospace"
      }}
    >
      {children}
    </span>
  )
}

function InfoRow({ label, value, bold, valueColor, mono }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '4px 0'
      }}
    >
      <span style={{ fontSize: 12, color: '#64748B' }}>{label}</span>
      <span
        style={{
          fontSize: 13,
          fontWeight: bold ? 700 : 500,
          color: valueColor || '#1E293B',
          fontFamily: mono ? "'DM Mono', monospace" : 'inherit'
        }}
      >
        {value}
      </span>
    </div>
  )
}

function ExistingPlanCard({ planLabel, serviceIds, packageFeeMonthly, billingId }) {
  const billingLabel = billingId
    ? BILLING_OPTIONS.find(b => b.id === billingId)?.label || billingId
    : '—'
  return (
    <Card style={{ marginBottom: 0 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: '#64748B',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          fontFamily: "'DM Mono', monospace",
          marginBottom: 12
        }}
      >
        Existing Plan
      </div>

      <InfoRow label="Plan" value={planLabel || '—'} />

      <div style={{ fontSize: 10, color: '#94A3B8', margin: '8px 0 4px' }}>Services</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
        {[...serviceIds].map(id => {
          const s = ALL_SERVICES.find(x => x.id === id)
          return s ? (
            <Tag key={id} color="#374151">
              {s.label}
            </Tag>
          ) : null
        })}
        {![...serviceIds].length && <span style={{ fontSize: 12, color: '#94A3B8' }}>—</span>}
      </div>

      <InfoRow
        label="Package fee (monthly)"
        value={fmt(Number(packageFeeMonthly) || 0)}
        mono
      />
      <InfoRow label="Billing cycle" value={billingLabel} />
    </Card>
  )
}

function StandardInputBox({ label, value, onChange, sublabel, hasError }) {
  return (
    <div style={{ flex: 1 }}>
      <div
        style={{ fontSize: 11, color: hasError ? '#ee362c' : '#64748B', marginBottom: 6 }}
      >
        {label}
      </div>
      <div style={{ position: 'relative' }}>
        <span
          style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: 14,
            color: '#64748B'
          }}
        >
          $
        </span>
        <input
          type="number"
          value={value}
          onChange={e => onChange(e.target.value ? Number(e.target.value) : '')}
          style={{
            background: '#ffffff',
            border: `1px solid ${hasError ? '#ee362c' : 'rgba(0, 0, 60, 0.2)'}`,
            boxSizing: 'border-box',
            boxShadow: 'inset 0px 4px 3px rgba(0, 0, 0, 0.02)',
            borderRadius: 3,
            height: 40,
            padding: '0 12px 0 24px',
            fontSize: 14,
            width: '100%',
            outline: 'none',
            color: '#0F172A'
          }}
        />
      </div>
      {sublabel && (
        <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 4 }}>{sublabel}</div>
      )}
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────
const ManagePlan = ({ isOpen, onClose, hotelDetails = {}, subsidiaryId, onSaveSuccess }) => {
  const dispatch = useDispatch()
  const dashboardUserId = useSelector(s => s.login.userId)
  const { addToast } = useToasts()
  const [isSaving, setIsSaving] = useState(false)
  const openBaselineRef = useRef(null)
  const prevSelectedPlanForNameRef = useRef()

  const subscriptions = hotelDetails?.subscriptions || []
  const paymentPeriod = hotelDetails?.paymentPeriod || ''
  const activeSubscriptions = subscriptions.filter(s => !s.endDate || new Date(s.endDate) > new Date())
  const hasPremiumSupport = activeSubscriptions.some(s => s.plan === 'PremiumSupport')
  const premiumSupportSub = activeSubscriptions.find(s => s.plan === 'PremiumSupport')
  const baseSub = activeSubscriptions.find(
    s => s.plan !== 'PremiumSupport' && s.plan !== 'SetupFee'
  )
  const setupSub = activeSubscriptions.find(s => s.plan === 'SetupFee')
  const hasMessaging = activeSubscriptions.some(
    s => s.plan === 'Messaging' || s.plan === 'Unlimited' || s.plan === 'Basic'
  )
  const currentPlanFromApi =
    activeSubscriptions.length > 2
      ? 'custom'
      : hasMessaging
      ? 'engagePlus'
      : 'engage'

  const billingIdFromApi = PAYMENT_PERIOD_TO_BILLING_ID[paymentPeriod] || ''

  const PROPERTY = {
    ...DEFAULT_PROPERTY,
    name: hotelDetails?.name || hotelDetails?.hotelName || DEFAULT_PROPERTY.name,
    id:
      hotelDetails?.displayId ||
      hotelDetails?.subsidiaryShortLinkToken ||
      DEFAULT_PROPERTY.id,
    address: hotelDetails?.billingAddress || DEFAULT_PROPERTY.address,
    rooms: hotelDetails?.numberOfRooms || DEFAULT_PROPERTY.rooms,
    csm: hotelDetails?.csmEmail || hotelDetails?.customerSuccessManager || DEFAULT_PROPERTY.csm,
    currentPlan: currentPlanFromApi,
    currentBilling: billingIdFromApi,
    currentServices: hasMessaging ? ['core_sms', 'inproperty'] : ['core_sms'],
    currentPackageFee: baseSub?.planRate ?? DEFAULT_PROPERTY.currentPackageFee,
    currentSetupFee: setupSub?.planRate ?? 0,
    premiumSupport: hasPremiumSupport,
    contractEnd: hotelDetails?.serviceStartDate
      ? new Date(hotelDetails.serviceStartDate).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        })
      : DEFAULT_PROPERTY.contractEnd
  }

  const [selectedPlan, setSelectedPlan] = useState(PROPERTY.currentPlan)
  const [billing, setBilling] = useState('')
  const [services, setServices] = useState(new Set(PROPERTY.currentServices))
  const [packageFee, setPackageFee] = useState(PROPERTY.currentPackageFee ?? '')
  const [setupFee, setSetupFee] = useState(PROPERTY.currentSetupFee ?? '')
  const [premiumSupportExplicit, setPremiumSupportExplicit] = useState(null)
  const [premiumSupportFee, setPremiumSupportFee] = useState(
    premiumSupportSub?.planRate ?? ''
  )
  const [pricingTouched, setPricingTouched] = useState(false)
  const [showAdvancedBilling, setShowAdvancedBilling] = useState(false)
  const [billingInteracted, setBillingInteracted] = useState(false)
  const [customPlanName, setCustomPlanName] = useState('')
  const [planDisplayNameTouched, setPlanDisplayNameTouched] = useState(false)
  const [effectiveDate, setEffectiveDate] = useState('')
  const [autoPayEnabled, setAutoPayEnabled] = useState(null)
  const [gracePeriodDays, setGracePeriodDays] = useState('')
  const [validationErrors, setValidationErrors] = useState([])
  const [existingPlanSnapshot, setExistingPlanSnapshot] = useState(null)
  const [showDowngradeConfirm, setShowDowngradeConfirm] = useState(false)

  const premiumSupport = premiumSupportExplicit === 'yes'

  useEffect(() => {
    if (isOpen && hotelDetails) {
      const baselineServices = hasMessaging ? ['core_sms', 'inproperty'] : ['core_sms']
      const initialGrace =
        hotelDetails.gracePeriodDays != null
          ? String(hotelDetails.gracePeriodDays)
          : hotelDetails.invoicePayInDays != null
          ? String(hotelDetails.invoicePayInDays)
          : ''
      const existingPlanLabel =
        PLANS.find(p => p.id === currentPlanFromApi)?.shortLabel || '—'
      const autoPayAtOpen = !!hotelDetails.isAutoPayEnabled
      const customPlanNameAtOpen =
        currentPlanFromApi === 'custom'
          ? getSuggestedEngageStylePlanName(new Set(baselineServices))
          : PLANS.find(p => p.id === currentPlanFromApi)?.shortLabel || ''
      openBaselineRef.current = {
        plan: currentPlanFromApi,
        billing: billingIdFromApi || '',
        servicesStr: JSON.stringify([...baselineServices].sort()),
        packageFee: baseSub?.planRate ?? '',
        setupFee: setupSub?.planRate ?? '',
        premiumExplicit: hasPremiumSupport ? 'yes' : 'no',
        premiumFee: premiumSupportSub?.planRate ?? '',
        autoPay: autoPayAtOpen,
        gracePeriodDays: initialGrace,
        customPlanName: customPlanNameAtOpen
      }
      setExistingPlanSnapshot({
        planId: currentPlanFromApi,
        planLabel: existingPlanLabel,
        serviceIds: [...baselineServices],
        packageFee: baseSub?.planRate ?? 0,
        setupFee: setupSub?.planRate ?? 0,
        premiumSupport: hasPremiumSupport,
        premiumSupportFee: premiumSupportSub?.planRate ?? 0,
        billingId: billingIdFromApi || '',
        autoPay: autoPayAtOpen,
        gracePeriodDaysStr: initialGrace
      })
      setSelectedPlan(currentPlanFromApi)
      setBilling('')
      setBillingInteracted(false)
      setShowAdvancedBilling(false)
      setServices(new Set(baselineServices))
      setPackageFee('')
      setSetupFee('')
      setPricingTouched(false)
      setPremiumSupportExplicit(null)
      setPremiumSupportFee('')
      setPlanDisplayNameTouched(false)
      setCustomPlanName(customPlanNameAtOpen)
      setEffectiveDate('')
      setAutoPayEnabled(autoPayAtOpen)
      setGracePeriodDays(initialGrace)
      setShowDowngradeConfirm(false)
    }
  }, [isOpen, subsidiaryId])

  // When plan changes: reset services to locked set for Engage / Engage+.
  // Custom preserves whatever services were already selected (e.g. Engage+ -> Custom).
  useEffect(() => {
    if (selectedPlan === 'custom') {
      return
    }
    const locked = new Set(PLAN_LOCKED[selectedPlan])
    setServices(new Set(locked))
    const planDef = PLANS.find(p => p.id === selectedPlan)
    if (planDef) {
      setCustomPlanName(planDef.shortLabel)
    }
  }, [selectedPlan])

  const suggestedPlanDisplayName = useMemo(
    () => getSuggestedEngageStylePlanName(services),
    [services]
  )

  useEffect(() => {
    if (!isOpen) {
      prevSelectedPlanForNameRef.current = selectedPlan
      return
    }
    const previous = prevSelectedPlanForNameRef.current
    prevSelectedPlanForNameRef.current = selectedPlan
    if (
      previous !== undefined &&
      previous !== selectedPlan &&
      selectedPlan === 'custom' &&
      previous !== 'custom'
    ) {
      setPlanDisplayNameTouched(false)
    }
  }, [isOpen, selectedPlan])

  useEffect(() => {
    if (!isOpen || selectedPlan !== 'custom') return
    if (!planDisplayNameTouched) {
      setCustomPlanName(suggestedPlanDisplayName)
    }
  }, [isOpen, selectedPlan, suggestedPlanDisplayName, planDisplayNameTouched])

  const toggleService = id => {
    const locked = PLAN_LOCKED[selectedPlan]
    if (locked.includes(id)) return // Can't uncheck locked

    const svc = ALL_SERVICES.find(s => s.id === id)
    const addingAddon = svc?.isAddon && !services.has(id)

    // Auto-upgrade to Engage+ if adding in-property to Engage
    if (selectedPlan === 'engage' && id === 'inproperty') {
      setSelectedPlan('engagePlus')
      return
    }

    if (addingAddon) {
      setSelectedPlan('custom')
    }

    setServices(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const monthlyRecurringTotal =
    (Number(packageFee) || 0) +
    (premiumSupport ? Number(premiumSupportFee) || 0 : 0)
  const grandTotal = monthlyRecurringTotal
  const currentPremiumMonthly = hasPremiumSupport
    ? Number(premiumSupportSub?.planRate) || 0
    : 0
  const currentMonthlyRecurring =
    (Number(PROPERTY.currentPackageFee) || 0) + currentPremiumMonthly
  const monthlyRecurringDiff = monthlyRecurringTotal - currentMonthlyRecurring
  const isMonthlyIncrease = monthlyRecurringDiff > 0
  const isDowngrade = existingPlanSnapshot
    ? isDowngradeChange({
        previousServiceIds: existingPlanSnapshot.serviceIds,
        currentServiceIds: services,
        previousMonthlyTotal: currentMonthlyRecurring,
        newMonthlyTotal: monthlyRecurringTotal
      })
    : false
  const setupTotal = Number(setupFee) || 0
  const periodMonths = billing ? BILLING_PERIOD_MONTHS[billing] : 0
  const periodInvoiceTotal = grandTotal * periodMonths

  const chargeSchedulePreview = useMemo(() => {
    if (!effectiveDate || !billing) return []
    const monthsInPeriod = BILLING_PERIOD_MONTHS[billing] || 1
    const periodAmount = monthlyRecurringTotal * monthsInPeriod
    let cursor = new Date(effectiveDate + 'T12:00:00')
    if (Number.isNaN(cursor.getTime())) return []
    const rows = []
    if (setupTotal > 0) {
      rows.push({
        date: new Date(cursor),
        amount: setupTotal,
        kind: 'Setup (one-time)'
      })
    }
    const periodsInYear = Math.ceil(12 / monthsInPeriod)
    for (let i = 0; i < periodsInYear; i++) {
      rows.push({
        date: new Date(cursor),
        amount: periodAmount,
        kind: 'Recurring'
      })
      cursor = addMonthsSafe(cursor, monthsInPeriod)
    }
    return rows
  }, [effectiveDate, billing, monthlyRecurringTotal, setupTotal])

  const billingDiffers = !!billingIdFromApi && !!billing && billing !== billingIdFromApi
  const feeNotExceeding =
    packageFee !== '' && packageFee !== null && packageFee !== undefined &&
    monthlyRecurringDiff <= 0
  const hasFirstInvoiceBeenPaid = !!hotelDetails?.isFirstPaymentMade

  const nextCycleStartDate = useMemo(() => {
    if (!billingIdFromApi) return null
    const currentPeriodMonths = BILLING_PERIOD_MONTHS[billingIdFromApi]
    if (!currentPeriodMonths) return null
    const anchorStr = baseSub?.startDate || hotelDetails?.serviceStartDate
    if (!anchorStr) return null
    const anchor = new Date(anchorStr)
    if (isNaN(anchor.getTime())) return null
    const now = new Date()
    let cursor = new Date(anchor)
    while (cursor <= now) {
      cursor = addMonthsSafe(cursor, currentPeriodMonths)
    }
    cursor.setDate(1)
    return cursor
  }, [billingIdFromApi, baseSub?.startDate, hotelDetails?.serviceStartDate])

  const shouldLockEffectiveDate =
    (billingDiffers ||
      shouldLockDowngradeToNextCycle({
        feeNotExceeding,
        isFirstPaymentMade: hasFirstInvoiceBeenPaid
      })) &&
    !!nextCycleStartDate

  useEffect(() => {
    if (!isOpen) return
    if (shouldLockEffectiveDate) {
      const yyyy = nextCycleStartDate.getFullYear()
      const mm = String(nextCycleStartDate.getMonth() + 1).padStart(2, '0')
      const dd = String(nextCycleStartDate.getDate()).padStart(2, '0')
      setEffectiveDate(`${yyyy}-${mm}-${dd}`)
    }
  }, [isOpen, shouldLockEffectiveDate, nextCycleStartDate])

  const baseline = openBaselineRef.current
  const planChanged = baseline && selectedPlan !== baseline.plan
  const billingChanged =
    baseline && billingInteracted && billing !== baseline.billing
  const servicesChanged =
    baseline &&
    JSON.stringify([...services].sort()) !== baseline.servicesStr
  const packageChanged =
    baseline &&
    pricingTouched &&
    (Number(packageFee) || 0) !== (Number(baseline.packageFee) || 0)
  const setupChanged =
    baseline &&
    pricingTouched &&
    (Number(setupFee) || 0) !== (Number(baseline.setupFee) || 0)
  const premiumChanged =
    baseline &&
    premiumSupportExplicit !== null &&
    (premiumSupportExplicit !== baseline.premiumExplicit ||
      (premiumSupportExplicit === 'yes' &&
        (Number(premiumSupportFee) || 0) !== (Number(baseline.premiumFee) || 0)))
  const autoPayChanged =
    baseline && autoPayEnabled !== baseline.autoPay
  const graceChanged =
    baseline &&
    String(gracePeriodDays) !== String(baseline.gracePeriodDays ?? '')
  const apiCustomPlanNameForCompare =
    selectedPlan === 'custom'
      ? String(customPlanName).trim() || suggestedPlanDisplayName
      : String(customPlanName).trim()
  const customPlanNameChanged =
    baseline &&
    apiCustomPlanNameForCompare !== String(baseline.customPlanName ?? '').trim()
  const isChanged =
    !!baseline &&
    (planChanged ||
      billingChanged ||
      servicesChanged ||
      packageChanged ||
      setupChanged ||
      premiumChanged ||
      autoPayChanged ||
      graceChanged ||
      customPlanNameChanged)

  const basePlanData = PLANS.find(p => p.id === selectedPlan)

  const getDynamicPlanData = () => {
    if (selectedPlan !== 'custom') return basePlanData

    return {
      ...basePlanData,
      label: 'Custom',
      shortLabel: 'Custom'
    }
  }

  const planData = getDynamicPlanData()

  const handleConfirmChange = async () => {
    const apiBillingCycle = BILLING_ID_TO_API[billing] || 'Monthly'
    const customPlanNameForApi =
      selectedPlan === 'custom'
        ? String(customPlanName).trim() || suggestedPlanDisplayName
        : String(customPlanName).trim()
    const payload = buildManagePlanPayload({
      serviceIds: [...services],
      billingCycle: apiBillingCycle,
      billingId: billing,
      packageFee: Number(packageFee) || 0,
      setupFee: Number(setupFee) || 0,
      premiumSupport,
      premiumSupportFee,
      effectiveDate,
      gracePeriodDays,
      autoPayEnabled,
      customPlanName: customPlanNameForApi,
      subsidiaryId,
      dashboardUserId
    })

    setIsSaving(true)
    try {
      await dispatch(updatePlan(payload))
      if (subsidiaryId != null && subsidiaryId !== '' && billing === 'monthly' && autoPayEnabled !== null) {
        await hotelApi.updateAsAdmin({ isAutoPayEnabled: !!autoPayEnabled }, subsidiaryId)
      }
      addToast('Plan updated successfully.', { appearance: 'success', autoDismiss: true })
      onClose()
      if (subsidiaryId) {
        dispatch(getOtherHotelDetails(subsidiaryId))
      }
      if (onSaveSuccess) onSaveSuccess()
    } catch (err) {
      addToast(err?.message || 'Failed to update plan. Please try again.', {
        appearance: 'error',
        autoDismiss: true
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleApply = async () => {
    const errors = []
    if (!billing) {
      errors.push('billing')
    }
    if (packageFee === '' || packageFee === null || packageFee === undefined) {
      errors.push('packageFee')
    }
    if (setupFee === '' || setupFee === null || setupFee === undefined) {
      errors.push('setupFee')
    }
    if (premiumSupportExplicit !== 'yes' && premiumSupportExplicit !== 'no') {
      errors.push('premiumSupportChoice')
    }
    if (
      premiumSupportExplicit === 'yes' &&
      (premiumSupportFee === '' ||
        premiumSupportFee === null ||
        premiumSupportFee === undefined)
    ) {
      errors.push('premiumSupportFee')
    }
    if (!effectiveDate) {
      errors.push('effectiveDate')
    }

    if (errors.length > 0) {
      setValidationErrors(errors)
      return
    }

    setValidationErrors([])

    if (
      shouldConfirmDowngradeNextCycle({
        isDowngrade,
        isFirstPaymentMade: hasFirstInvoiceBeenPaid
      })
    ) {
      setShowDowngradeConfirm(true)
      return
    }

    await handleConfirmChange()
  }

  const graceDaysNumeric =
    gracePeriodDays === '' || gracePeriodDays == null
      ? NaN
      : Number(gracePeriodDays)
  const graceShowOverRecommended =
    Number.isFinite(graceDaysNumeric) && graceDaysNumeric > GRACE_DAYS_RECOMMENDED_MAX
  const graceShowOverAbsolute =
    Number.isFinite(graceDaysNumeric) && graceDaysNumeric > GRACE_DAYS_WARNING_MAX

  if (!isOpen) return null

  return (
    <div className={styles.drawerOverlay}>
      <div className={styles.drawerContainer}>
        <div className={styles.drawerHeader}>
          <div className={styles.drawerTitle}>Manage Plan</div>
          <button className={styles.closeButton} onClick={onClose}>
            &times;
          </button>
        </div>

        <div className={styles.drawerContent}>
          <div
            style={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: 14,
              display: 'flex',
              gap: 20
            }}
          >
            {/* ── LEFT COLUMN ── */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* ── STEP 1: Plan ── */}
              <Card>
                <SectionTitle step="1" children="Select Plan" />
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 10
                  }}
                >
                  {PLANS.map(plan => {
                    const sel = selectedPlan === plan.id
                    const isCur = PROPERTY.currentPlan === plan.id
                    return (
                      <div
                        key={plan.id}
                        onClick={() => setSelectedPlan(plan.id)}
                        style={{
                          border: `2px solid ${sel ? plan.color : '#E2E8F0'}`,
                          borderRadius: 10,
                          padding: '14px 16px',
                          cursor: 'pointer',
                          background: sel ? plan.color + '08' : '#FAFAFA',
                          transition: 'all 0.12s',
                          position: 'relative'
                        }}
                      >
                        {isCur && (
                          <div
                            style={{
                              position: 'absolute',
                              top: -9,
                              left: 10,
                              background: '#0F172A',
                              color: '#fff',
                              fontSize: 9,
                              fontWeight: 700,
                              padding: '1px 7px',
                              borderRadius: 3,
                              fontFamily: "'DM Mono', monospace",
                              letterSpacing: '0.06em'
                            }}
                          >
                            CURRENT
                          </div>
                        )}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            marginBottom: 8
                          }}
                        >
                          <div
                            style={{
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              border: `2px solid ${sel ? plan.color : '#CBD5E1'}`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            {sel && (
                              <div
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  background: plan.color
                                }}
                              />
                            )}
                          </div>
                          <span
                            style={{ fontSize: 13, fontWeight: 700, color: '#1E293B' }}
                          >
                            {plan.shortLabel}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </Card>

              {(selectedPlan === 'engage' ||
                selectedPlan === 'engagePlus' ||
                selectedPlan === 'custom') && (
                <Card style={{ marginBottom: 16 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: '#1E293B',
                      marginBottom: 6
                    }}
                  >
                    Plan display name (optional)
                  </div>
                  {selectedPlan === 'custom' && (
                    <div
                      style={{
                        fontSize: 11,
                        color: '#64748B',
                        marginBottom: 8,
                        lineHeight: 1.45
                      }}
                    >
                      Suggested name updates when you change services (Engage vs Engage+ with Email
                      and/or Cart Abandonment). You can edit it anytime; the plan type stays Custom.
                    </div>
                  )}
                  <input
                    type="text"
                    value={customPlanName}
                    onChange={e => {
                      setPlanDisplayNameTouched(true)
                      setCustomPlanName(e.target.value)
                    }}
                    disabled={selectedPlan !== 'custom'}
                    placeholder={
                      selectedPlan === 'custom'
                        ? suggestedPlanDisplayName
                        : 'e.g. Enterprise messaging bundle'
                    }
                    style={{
                      background: selectedPlan !== 'custom' ? '#F1F5F9' : '#ffffff',
                      border: '1px solid rgba(0, 0, 60, 0.2)',
                      boxSizing: 'border-box',
                      boxShadow: 'inset 0px 4px 3px rgba(0, 0, 0, 0.02)',
                      borderRadius: 3,
                      height: 40,
                      padding: '0 12px',
                      fontSize: 14,
                      width: '100%',
                      outline: 'none',
                      color: selectedPlan !== 'custom' ? '#64748B' : '#0F172A',
                      fontFamily: 'inherit',
                      cursor: selectedPlan !== 'custom' ? 'not-allowed' : 'text'
                    }}
                  />
                  {selectedPlan === 'custom' && planDisplayNameTouched && (
                    <div
                      style={{
                        marginTop: 8,
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: 11,
                        color: '#64748B'
                      }}
                    >
                      <span>
                        Current suggestion:{' '}
                        <span style={{ fontWeight: 600, color: '#334155' }}>
                          {suggestedPlanDisplayName}
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setPlanDisplayNameTouched(false)
                          setCustomPlanName(suggestedPlanDisplayName)
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#4079F5',
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          padding: 0,
                          fontFamily: 'inherit'
                        }}
                      >
                        Apply suggestion
                      </button>
                    </div>
                  )}
                </Card>
              )}

              {/* ── STEP 2: Services ── */}
              <Card>
                <SectionTitle step="2" children="Services" />

                {/* Locked / Core Services */}
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#64748B',
                    letterSpacing: '0.08em',
                    marginBottom: 8,
                    textTransform: 'uppercase',
                    fontFamily: "'DM Mono', monospace"
                  }}
                >
                  Core Services
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    marginBottom: 16
                  }}
                >
                  {ALL_SERVICES.filter(s => !s.isAddon).map(svc => {
                    const locked = PLAN_LOCKED[selectedPlan].includes(svc.id)
                    const checked = services.has(svc.id)
                    const disabled = locked
                    return (
                      <label
                        key={svc.id}
                        onClick={() => !disabled && toggleService(svc.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          padding: '12px 14px',
                          borderRadius: 8,
                          border: `1.5px solid ${
                            checked ? (locked ? '#BBF7D0' : '#BFDBFE') : '#E2E8F0'
                          }`,
                          background: checked
                            ? locked
                              ? '#F0FDF4'
                              : '#EFF6FF'
                            : '#FAFAFA',
                          cursor: disabled ? 'default' : 'pointer',
                          transition: 'all 0.12s',
                          position: 'relative'
                        }}
                      >
                        <div
                          style={{
                            width: 18,
                            height: 18,
                            borderRadius: 4,
                            flexShrink: 0,
                            border: `2px solid ${
                              checked ? (locked ? '#10B981' : '#4079F5') : '#CBD5E1'
                            }`,
                            background: checked
                              ? locked
                                ? '#10B981'
                                : '#4079F5'
                              : '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {checked && (
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                              <path
                                d="M2 5L4.5 7.5L8 3"
                                stroke="#fff"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </div>
                        <span style={{ fontSize: 16 }}>{svc.icon}</span>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{ fontSize: 13, fontWeight: 600, color: '#1E293B' }}
                          >
                            {svc.label}
                          </div>
                        </div>
                        {locked && (
                          <span
                            style={{
                              fontSize: 9,
                              fontWeight: 700,
                              color: '#059669',
                              background: '#ECFDF5',
                              border: '1px solid #A7F3D0',
                              borderRadius: 4,
                              padding: '2px 6px',
                              fontFamily: "'DM Mono', monospace",
                              letterSpacing: '0.06em'
                            }}
                          >
                            INCLUDED
                          </span>
                        )}
                      </label>
                    )
                  })}
                </div>

                {/* Add-ons */}
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#64748B',
                    letterSpacing: '0.08em',
                    marginBottom: 8,
                    textTransform: 'uppercase',
                    fontFamily: "'DM Mono', monospace"
                  }}
                >
                  Optional Add-ons
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {ALL_SERVICES.filter(s => s.isAddon).map(svc => {
                    const checked = services.has(svc.id)
                    return (
                      <label
                        key={svc.id}
                        onClick={() => toggleService(svc.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          padding: '12px 14px',
                          borderRadius: 8,
                          border: `1.5px solid ${checked ? '#BFDBFE' : '#E2E8F0'}`,
                          background: checked ? '#EFF6FF' : '#FAFAFA',
                          cursor: 'pointer',
                          transition: 'all 0.12s'
                        }}
                      >
                        <div
                          style={{
                            width: 18,
                            height: 18,
                            borderRadius: 4,
                            flexShrink: 0,
                            border: `2px solid ${checked ? '#4079F5' : '#CBD5E1'}`,
                            background: checked ? '#4079F5' : '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {checked && (
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                              <path
                                d="M2 5L4.5 7.5L8 3"
                                stroke="#fff"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </div>
                        <span style={{ fontSize: 16 }}>{svc.icon}</span>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{ fontSize: 13, fontWeight: 600, color: '#1E293B' }}
                          >
                            {svc.label}
                          </div>
                        </div>
                      </label>
                    )
                  })}
                </div>
              </Card>

              {/* ── STEP 3: Pricing & Fees ── */}
              <Card>
                <SectionTitle step="3" children="Pricing & Fees" />

                <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                  <StandardInputBox
                    label="Package fee (monthly)"
                    value={packageFee}
                    onChange={v => {
                      setPricingTouched(true)
                      setPackageFee(v)
                    }}
                    hasError={validationErrors.includes('packageFee')}
                  />
                  <StandardInputBox
                    label="One-Time Setup Fee"
                    value={setupFee}
                    onChange={v => {
                      setPricingTouched(true)
                      setSetupFee(v)
                    }}
                    hasError={validationErrors.includes('setupFee')}
                  />
                </div>

                <div
                  style={{
                    marginBottom: 12,
                    border:
                      validationErrors.includes('premiumSupportChoice') ||
                      validationErrors.includes('premiumSupportFee')
                        ? '1px solid #ee362c'
                        : '1px solid transparent',
                    borderRadius: 8,
                    padding:
                      validationErrors.includes('premiumSupportChoice') ||
                      validationErrors.includes('premiumSupportFee')
                        ? 12
                        : 0
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#64748B',
                      marginBottom: 10,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      fontFamily: "'DM Mono', monospace"
                    }}
                  >
                    Premium support
                  </div>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      marginBottom: 10,
                      cursor: 'pointer',
                      fontSize: 13,
                      color: '#1E293B',
                      fontWeight: 500
                    }}
                  >
                    <input
                      type="radio"
                      name="managePlanPremiumSupport"
                      checked={premiumSupportExplicit === 'yes'}
                      onChange={() => setPremiumSupportExplicit('yes')}
                      style={{ width: 16, height: 16, flexShrink: 0 }}
                    />
                    Include premium support
                  </label>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      marginBottom: 12,
                      cursor: 'pointer',
                      fontSize: 13,
                      color: '#1E293B',
                      fontWeight: 500
                    }}
                  >
                    <input
                      type="radio"
                      name="managePlanPremiumSupport"
                      checked={premiumSupportExplicit === 'no'}
                      onChange={() => {
                        setPremiumSupportExplicit('no')
                        setPremiumSupportFee('')
                      }}
                      style={{ width: 16, height: 16, flexShrink: 0 }}
                    />
                    Do not include premium support
                  </label>

                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: 11,
                        color: validationErrors.includes('premiumSupportFee')
                          ? '#ee362c'
                          : '#64748B',
                        marginBottom: 6
                      }}
                    >
                      Monthly premium support fee
                    </div>
                    <div style={{ position: 'relative' }}>
                      <span
                        style={{
                          position: 'absolute',
                          left: 12,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          fontSize: 14,
                          color: '#64748B'
                        }}
                      >
                        $
                      </span>
                      <input
                        type="number"
                        value={premiumSupportFee}
                        onChange={e =>
                          setPremiumSupportFee(
                            e.target.value ? Number(e.target.value) : ''
                          )
                        }
                        disabled={premiumSupportExplicit !== 'yes'}
                        style={{
                          background:
                            premiumSupportExplicit === 'yes' ? '#ffffff' : '#E9EAED',
                          border: `1px solid ${
                            validationErrors.includes('premiumSupportFee')
                              ? '#ee362c'
                              : 'rgba(0, 0, 60, 0.2)'
                          }`,
                          boxSizing: 'border-box',
                          boxShadow: 'inset 0px 4px 3px rgba(0, 0, 0, 0.02)',
                          borderRadius: 3,
                          height: 40,
                          padding: '0 12px 0 24px',
                          fontSize: 14,
                          width: '100%',
                          outline: 'none',
                          color:
                            premiumSupportExplicit === 'yes' ? '#0F172A' : '#94A3B8',
                          cursor:
                            premiumSupportExplicit === 'yes' ? 'text' : 'not-allowed'
                        }}
                      />
                    </div>
                  </div>
                </div>
              </Card>

              {/* ── STEP 4: Billing Cycle ── */}
              <Card
                style={{
                  border:
                    !billing && validationErrors.includes('billing')
                      ? '1px solid #ee362c'
                      : '1px solid #E2E8F0'
                }}
              >
                <SectionTitle step="4" children="Billing Cycle" />
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 8
                  }}
                >
                  {BILLING_OPTIONS_MAIN.map(opt => {
                    const sel = billing === opt.id
                    const hasBillingError = validationErrors.includes('billing')
                    return (
                      <div
                        key={opt.id}
                        onClick={() => {
                          setBillingInteracted(true)
                          setBilling(opt.id)
                        }}
                        style={{
                          border: `2px solid ${
                            sel ? '#4079F5' : hasBillingError ? '#ee362c' : '#E2E8F0'
                          }`,
                          borderRadius: 10,
                          padding: '12px 14px',
                          cursor: 'pointer',
                          background: sel
                            ? '#4079F508'
                            : hasBillingError
                            ? '#FEF2F2'
                            : '#FAFAFA',
                          transition: 'all 0.12s',
                          position: 'relative'
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8
                          }}
                        >
                          <div
                            style={{
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              border: `2px solid ${sel ? '#4079F5' : '#CBD5E1'}`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            {sel && (
                              <div
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  background: '#4079F5'
                                }}
                              />
                            )}
                          </div>
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: '#1E293B'
                            }}
                          >
                            {opt.label}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>
                <div style={{ marginTop: 14 }}>
                  <button
                    type="button"
                    onClick={() => setShowAdvancedBilling(v => !v)}
                    style={{
                      border: 'none',
                      background: 'none',
                      color: '#4079F5',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      padding: 0,
                      textDecoration: 'underline',
                      fontFamily: 'inherit'
                    }}
                  >
                    {showAdvancedBilling ? 'Hide' : 'Show'} advanced billing options
                  </button>
                  {showAdvancedBilling && (
                    <div style={{ marginTop: 12 }}>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          setBillingInteracted(true)
                          setBilling(BILLING_OPTION_MONTHLY.id)
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setBillingInteracted(true)
                            setBilling(BILLING_OPTION_MONTHLY.id)
                          }
                        }}
                        style={{
                          border: `2px solid ${
                            billing === BILLING_OPTION_MONTHLY.id
                              ? '#4079F5'
                              : validationErrors.includes('billing')
                              ? '#ee362c'
                              : '#E2E8F0'
                          }`,
                          borderRadius: 10,
                          padding: '12px 14px',
                          cursor: 'pointer',
                          background:
                            billing === BILLING_OPTION_MONTHLY.id
                              ? '#4079F508'
                              : '#FAFAFA',
                          maxWidth: 220,
                          transition: 'all 0.12s'
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8
                          }}
                        >
                          <div
                            style={{
                              width: 16,
                                height: 16,
                                borderRadius: '50%',
                                border: `2px solid ${
                                  billing === BILLING_OPTION_MONTHLY.id
                                    ? '#4079F5'
                                    : '#CBD5E1'
                                }`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                              }}
                            >
                              {billing === BILLING_OPTION_MONTHLY.id && (
                                <div
                                  style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    background: '#4079F5'
                                  }}
                                />
                              )}
                            </div>
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: 700,
                                color: '#1E293B'
                              }}
                            >
                              {BILLING_OPTION_MONTHLY.label}
                            </span>
                          </div>
                      </div>
                    </div>
                  )}
                </div>
                {!billing && validationErrors.includes('billing') && (
                  <div
                    style={{
                      color: '#ee362c',
                      fontSize: 12,
                      marginTop: 12,
                      fontWeight: 500
                    }}
                  >
                    Please select a billing cycle to continue.
                  </div>
                )}
              </Card>

              {/* ── STEP 5: Change Details ── */}
              <Card>
                <SectionTitle step="5" children="Change Details" />
                <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        fontSize: 12,
                        color: validationErrors.includes('effectiveDate')
                          ? '#ee362c'
                          : '#64748B',
                        display: 'block',
                        marginBottom: 5
                      }}
                    >
                      Effective date (required)
                    </label>
                    <input
                      type="date"
                      value={effectiveDate}
                      onChange={e => setEffectiveDate(e.target.value)}
                      disabled={shouldLockEffectiveDate}
                      style={{
                        background: shouldLockEffectiveDate ? '#E9EAED' : '#ffffff',
                        border: `1px solid ${
                          validationErrors.includes('effectiveDate')
                            ? '#ee362c'
                            : 'rgba(0, 0, 60, 0.2)'
                        }`,
                        boxSizing: 'border-box',
                        boxShadow: 'inset 0px 4px 3px rgba(0, 0, 0, 0.02)',
                        borderRadius: 3,
                        height: 40,
                        padding: '0 12px',
                        fontSize: 14,
                        width: '100%',
                        outline: 'none',
                        color: shouldLockEffectiveDate ? '#94A3B8' : '#0F172A',
                        fontFamily: 'inherit',
                        cursor: shouldLockEffectiveDate ? 'not-allowed' : 'text'
                      }}
                    />
                    {shouldLockEffectiveDate && billingDiffers && (
                      <div style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>
                        Billing cycle changed — effective date set to the start of the next cycle.
                      </div>
                    )}
                    {shouldLockEffectiveDate && !billingDiffers && feeNotExceeding && (
                      <div style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>
                        Fee does not exceed the current plan — effective date set to the start of the next cycle.
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#64748B',
                      marginBottom: 10,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      fontFamily: "'DM Mono', monospace"
                    }}
                  >
                    Autopay
                  </div>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      marginBottom: 10,
                      cursor: 'pointer',
                      fontSize: 13,
                      color: '#1E293B',
                      fontWeight: 500
                    }}
                  >
                    <input
                      type="radio"
                      name="managePlanAutoPay"
                      checked={autoPayEnabled === true}
                      onChange={() => setAutoPayEnabled(true)}
                      style={{ width: 16, height: 16, flexShrink: 0 }}
                    />
                    Enable autopay
                  </label>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      cursor: 'pointer',
                      fontSize: 13,
                      color: '#1E293B',
                      fontWeight: 500
                    }}
                  >
                    <input
                      type="radio"
                      name="managePlanAutoPay"
                      checked={autoPayEnabled === false}
                      onChange={() => setAutoPayEnabled(false)}
                      style={{ width: 16, height: 16, flexShrink: 0 }}
                    />
                    Disable autopay
                  </label>
                </div>
                <div>
                  <label
                    style={{
                      fontSize: 12,
                      color: '#64748B',
                      display: 'block',
                      marginBottom: 5
                    }}
                  >
                    Grace period (days)
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={gracePeriodDays}
                    onChange={e => setGracePeriodDays(e.target.value)}
                    placeholder="e.g. 14"
                    style={{
                      background: '#ffffff',
                      border: '1px solid rgba(0, 0, 60, 0.2)',
                      boxSizing: 'border-box',
                      boxShadow: 'inset 0px 4px 3px rgba(0, 0, 0, 0.02)',
                      borderRadius: 3,
                      height: 40,
                      padding: '0 12px',
                      fontSize: 14,
                      width: '100%',
                      maxWidth: 200,
                      outline: 'none',
                      color: '#0F172A',
                      fontFamily: 'inherit'
                    }}
                  />
                  {graceShowOverRecommended && !graceShowOverAbsolute && (
                    <div
                      style={{
                        fontSize: 11,
                        color: '#B45309',
                        marginTop: 8,
                        lineHeight: 1.45,
                        background: '#FFFBEB',
                        border: '1px solid #FDE68A',
                        borderRadius: 6,
                        padding: '8px 10px'
                      }}
                    >
                      Values above {GRACE_DAYS_RECOMMENDED_MAX} days are outside the usual range.
                    </div>
                  )}
                  {graceShowOverAbsolute && (
                    <div
                      style={{
                        fontSize: 11,
                        color: '#B45309',
                        marginTop: 8,
                        lineHeight: 1.45,
                        background: '#FFFBEB',
                        border: '1px solid #F59E0B',
                        borderRadius: 6,
                        padding: '8px 10px'
                      }}
                    >
                      Above {GRACE_DAYS_WARNING_MAX} days is uncommon; confirm with billing policy.
                      You can still save.
                    </div>
                  )}
                  <div
                    style={{
                      fontSize: 11,
                      color: '#713F12',
                      marginTop: 10,
                      lineHeight: 1.5,
                      background: '#FEFCE8',
                      border: '1px solid #FDE047',
                      borderRadius: 6,
                      padding: '10px 12px'
                    }}
                  >
                    Days after the due date before we pause services if the invoice is still unpaid.
                  </div>
                </div>
              </Card>
            </div>

            {/* ── RIGHT SUMMARY COLUMN ── */}
            <div style={{ width: 320, flexShrink: 0 }}>
              <div
                style={{
                  position: 'sticky',
                  top: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14
                }}
              >
                {existingPlanSnapshot && (
                  <ExistingPlanCard
                    planLabel={existingPlanSnapshot.planLabel}
                    serviceIds={existingPlanSnapshot.serviceIds}
                    packageFeeMonthly={existingPlanSnapshot.packageFee}
                    billingId={existingPlanSnapshot.billingId}
                  />
                )}

                <Card style={{ marginBottom: 0 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#64748B',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      fontFamily: "'DM Mono', monospace",
                      marginBottom: 12
                    }}
                  >
                    Updated Plan
                  </div>

                  <InfoRow label="Plan" value={planData?.shortLabel || '—'} />
                  {(selectedPlan === 'custom' ||
                    (customPlanName.trim() !== '' &&
                      (selectedPlan === 'engage' || selectedPlan === 'engagePlus'))) && (
                    <InfoRow
                      label="Plan display name"
                      value={
                        selectedPlan === 'custom'
                          ? customPlanName.trim() || suggestedPlanDisplayName
                          : customPlanName.trim()
                      }
                    />
                  )}

                  <div style={{ fontSize: 10, color: '#94A3B8', margin: '8px 0 4px' }}>Services</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 14 }}>
                    {[...services].map(id => {
                      const s = ALL_SERVICES.find(x => x.id === id)
                      return s ? (
                        <Tag key={id} color="#374151">
                          {s.label}
                        </Tag>
                      ) : null
                    })}
                    {![...services].length && (
                      <span style={{ fontSize: 12, color: '#94A3B8' }}>—</span>
                    )}
                  </div>

                  <InfoRow
                    label="Package fee (monthly)"
                    value={fmt(Number(packageFee) || 0)}
                    mono
                  />

                  {[...services]
                    .filter(id => !PLAN_LOCKED[selectedPlan].includes(id))
                    .map(id => {
                      const s = ALL_SERVICES.find(x => x.id === id)
                      return s ? (
                        <InfoRow
                          key={id}
                          label={`Add-on: ${s.label}`}
                          value="Included"
                          valueColor="#10B981"
                        />
                      ) : null
                    })}

                  {premiumSupport && (
                    <InfoRow
                      label="Add-on: Premium support (monthly)"
                      value={`+${fmt(Number(premiumSupportFee) || 0)}/mo`}
                      mono
                    />
                  )}

                  <InfoRow
                    label="Setup fee (one-time)"
                    value={fmt(Number(setupFee) || 0)}
                    mono
                  />

                  <Divider />

                  <InfoRow
                    label="Total monthly (recurring)"
                    value={fmt(grandTotal)}
                    bold
                    mono
                  />
                  <InfoRow
                    label="Billing cycle"
                    value={
                      billing
                        ? BILLING_OPTIONS.find(b => b.id === billing)?.label
                        : 'Not selected'
                    }
                  />
                  {billing && periodMonths > 0 && (
                    <InfoRow
                      label="Invoice period total (estimate)"
                      value={fmt(periodInvoiceTotal)}
                      bold
                      mono
                    />
                  )}
                  <InfoRow
                    label="Autopay"
                    value={autoPayEnabled === true ? 'On' : 'Off'}
                  />
                  {gracePeriodDays !== '' && (
                    <InfoRow label="Grace period" value={`${gracePeriodDays} days`} />
                  )}

                  {isChanged && monthlyRecurringDiff !== 0 && (
                    <div
                      style={{
                        marginTop: 12,
                        padding: '10px 12px',
                        borderRadius: 8,
                        background: isMonthlyIncrease ? '#EFF6FF' : '#FFF7ED',
                        border: `1px solid ${isMonthlyIncrease ? '#BFDBFE' : '#FED7AA'}`
                      }}
                    >
                      <div style={{ fontSize: 11, color: '#64748B', marginBottom: 2 }}>
                        {isMonthlyIncrease ? 'More' : 'Less'} charged per month
                      </div>
                      <div
                        style={{
                          fontSize: 16,
                          fontWeight: 700,
                          color: isMonthlyIncrease ? '#4079F5' : '#ee362c',
                          fontFamily: "'DM Mono', monospace"
                        }}
                      >
                        {isMonthlyIncrease ? '+' : ''}
                        {fmt(monthlyRecurringDiff)}/mo
                      </div>
                    </div>
                  )}
                </Card>

                {chargeSchedulePreview.length > 0 && (
                  <Card style={{ marginBottom: 0 }}>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#64748B',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        fontFamily: "'DM Mono', monospace",
                        marginBottom: 10
                      }}
                    >
                      Charge preview (12 mo)
                    </div>
                    <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                      <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ color: '#64748B', textAlign: 'left' }}>
                            <th style={{ padding: '4px 4px 6px 0', fontWeight: 600 }}>Date</th>
                            <th style={{ padding: '4px 0 6px 4px', fontWeight: 600 }}>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {chargeSchedulePreview.map((row, idx) => (
                            <tr key={idx} style={{ borderTop: '1px solid #F1F5F9' }}>
                              <td style={{ padding: '6px 4px 6px 0', color: '#334155' }}>
                                {row.date.toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </td>
                              <td
                                style={{
                                  padding: '6px 0 6px 4px',
                                  textAlign: 'right',
                                  fontFamily: "'DM Mono', monospace",
                                  color: '#0F172A'
                                }}
                              >
                                {fmt(row.amount)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                )}

                {validationErrors.length > 0 && (
                  <div
                    style={{
                      background: '#FEF2F2',
                      border: '1px solid #FECACA',
                      borderRadius: 10,
                      padding: '12px 14px',
                      fontSize: 12,
                      color: '#DC2626',
                      lineHeight: 1.4,
                      marginBottom: 16,
                      fontWeight: 500
                    }}
                  >
                    <ul style={{ margin: 0, paddingLeft: 16, listStyleType: 'disc' }}>
                      {validationErrors.includes('billing') && (
                        <li style={{ marginBottom: 4 }}>
                          Please select a billing cycle.
                        </li>
                      )}
                      {validationErrors.includes('packageFee') && (
                        <li style={{ marginBottom: 4 }}>
                          Please enter the package fee as per the contract with the
                          customer.
                        </li>
                      )}
                      {validationErrors.includes('setupFee') && (
                        <li style={{ marginBottom: 4 }}>
                          Please set the one-time setup fee. You can enter 0 if the fee is
                          waived.
                        </li>
                      )}
                      {validationErrors.includes('premiumSupportFee') && (
                        <li style={{ marginBottom: 4 }}>
                          Please enter the monthly premium support fee.
                        </li>
                      )}
                      {validationErrors.includes('premiumSupportChoice') && (
                        <li style={{ marginBottom: 4 }}>
                          Please choose whether premium support is included or not.
                        </li>
                      )}
                      {validationErrors.includes('effectiveDate') && (
                        <li style={{ marginBottom: 4 }}>
                          Effective date is required — select when the change takes effect.
                        </li>
                      )}
                    </ul>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => isChanged && handleApply()}
                  disabled={!isChanged || isSaving}
                  style={{
                    width: '100%',
                    padding: '13px',
                    borderRadius: 3,
                    border: 'none',
                    background: isChanged && !isSaving ? '#0079ff' : '#E2E8F0',
                    color: isChanged && !isSaving ? '#fff' : '#94A3B8',
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: isChanged && !isSaving ? 'pointer' : 'not-allowed',
                    transition: 'all 0.15s'
                  }}
                >
                  {!isChanged ? 'No changes' : isSaving ? 'Saving…' : 'Apply'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showDowngradeConfirm && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.55)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={() => setShowDowngradeConfirm(false)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: '28px 32px 24px',
              maxWidth: 460,
              width: '90%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
              position: 'relative'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 16
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: '#FFF7ED',
                  border: '1px solid #FED7AA',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  flexShrink: 0
                }}
              >
                ⚠
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1E293B' }}>
                Confirm Plan Downgrade
              </div>
            </div>

            <div
              style={{
                fontSize: 13,
                color: '#334155',
                lineHeight: 1.6,
                marginBottom: 24
              }}
            >
              The current billing cycle has already been charged for the{' '}
              <strong>{existingPlanSnapshot?.planLabel || 'current'}</strong> plan.
              This downgrade will be applied starting from the next billing cycle.
              <br />
              <br />
              Would you like to proceed with the downgrade?
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                type="button"
                onClick={() => setShowDowngradeConfirm(false)}
                style={{
                  padding: '9px 20px',
                  borderRadius: 6,
                  border: '1px solid #E2E8F0',
                  background: '#fff',
                  color: '#475569',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSaving}
                onClick={async () => {
                  setShowDowngradeConfirm(false)
                  await handleConfirmChange()
                }}
                style={{
                  padding: '9px 20px',
                  borderRadius: 6,
                  border: 'none',
                  background: '#DC2626',
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit'
                }}
              >
                {isSaving ? 'Saving…' : 'Yes, Downgrade'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ManagePlan
